import type { ApolloClient } from '@apollo/client';
import { browserLogger } from '@newfront-insurance/dd-rum';

import type { UIMessageWithMetadata } from '../../../lib/types';
import { GET_CHAT_WITH_MESSAGES, type GetChatWithMessagesMessage } from '../../../queries/chats';

/**
 * Normalizes a chat UUID by removing the 'new-chat-' prefix if present.
 * This ensures consistent UUID handling across the codebase.
 */
export function normalizeChatUuid(chatUuid: string): string {
  return chatUuid.replace('new-chat-', '');
}

function toCachedMessage({
  chatUuid,
  message,
  cachedMessage,
}: {
  chatUuid: string;
  message: UIMessageWithMetadata;
  cachedMessage?: GetChatWithMessagesMessage;
}): GetChatWithMessagesMessage {
  const metadata = message.metadata ?? { files: [] };
  const metadataFiles = Array.isArray(metadata.files) ? metadata.files : [];
  const reaction = metadata.reaction === 'THUMBS_UP' || metadata.reaction === 'THUMBS_DOWN' ? metadata.reaction : null;

  return {
    ...cachedMessage,
    uuid: message.id,
    chatUuid: cachedMessage?.chatUuid ?? normalizeChatUuid(chatUuid),
    role: message.role,
    reaction: cachedMessage?.reaction ?? reaction,
    files:
      metadataFiles.length > 0
        ? metadataFiles.map((file) => ({ uuid: file.uuid, filename: file.filename }))
        : (cachedMessage?.files ?? []),
    parts: message.parts,
    metadata,
    createdAt: cachedMessage?.createdAt ?? metadata.createdAt ?? new Date().toISOString(),
  } as GetChatWithMessagesMessage;
}

/** Replaces a cached assistant with the exact message delivered by AI SDK onFinish. */
export function syncFinishedMessageToCache({
  apolloClient,
  chatUuid,
  messageUuid,
  message,
}: {
  apolloClient: ApolloClient<unknown>;
  chatUuid: string;
  messageUuid: string;
  message: UIMessageWithMetadata;
}): void {
  if (messageUuid !== message.id) {
    browserLogger.warn('[Notification Helpers] Finished assistant message id changed during continuation', {
      chatUuid,
      messageUuid,
      finishedMessageUuid: message.id,
    });
  }

  for (const cacheUuid of new Set([normalizeChatUuid(chatUuid), chatUuid])) {
    try {
      const cachedData = apolloClient.readQuery({
        query: GET_CHAT_WITH_MESSAGES,
        variables: { uuid: cacheUuid },
      });

      if (!cachedData?.getChat?.messages) {
        continue;
      }

      let matched = false;
      const messages = cachedData.getChat.messages.map((cachedMessage) => {
        if (cachedMessage.uuid !== message.id) {
          return cachedMessage;
        }

        matched = true;
        return toCachedMessage({ chatUuid, message, cachedMessage });
      });

      // The cache may not contain in-session turns for an existing chat. Only
      // replace a known message; appending here could publish a partial history.
      if (!matched) {
        continue;
      }

      apolloClient.writeQuery({
        query: GET_CHAT_WITH_MESSAGES,
        variables: { uuid: cacheUuid },
        data: {
          getChat: {
            ...cachedData.getChat,
            messages,
          },
        },
      });
    } catch (error) {
      browserLogger.debug('[Notification Helpers] Could not synchronize the finished assistant message', {
        chatUuid,
        cacheUuid,
        messageUuid,
        error,
      });
    }
  }
}
